import { Button, Popover, PopoverContent, PopoverTrigger, cn } from '@command-center/ui-system';
import {
  Activity,
  Bot,
  Check,
  ChevronDown,
  Command,
  LogOut,
  Search,
  Settings,
  UserRound,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

export function SidebarCommandButton({
  compact,
  onOpen,
}: {
  compact: boolean;
  onOpen: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'h-10 w-full justify-start border-border bg-background px-3 text-muted-foreground shadow-sm hover:text-foreground',
        compact && 'justify-center px-0',
      )}
      onClick={onOpen}
      aria-label="Open command palette"
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      {!compact && (
        <>
          <span className="truncate text-xs">Search or run a command</span>
          <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 text-[10px]">
            <Command className="h-2.5 w-2.5" aria-hidden />K
          </kbd>
        </>
      )}
    </Button>
  );
}

export function WorkspaceMenu({
  compact,
  organizationName,
  userName,
  userEmail,
  onSignOut,
}: {
  compact: boolean;
  organizationName: string;
  userName?: string;
  userEmail?: string;
  onSignOut: () => void;
}) {
  const { pathname } = useLocation();
  const accountLabel = userName || userEmail || 'ECORP user';
  const initials =
    accountLabel
      .split(/\s|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'EC';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-h-12 w-full items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            compact && 'justify-center px-0',
          )}
          aria-label="Open workspace and account menu"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-semibold text-primary">
            {initials}
          </span>
          {!compact && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{organizationName}</span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  Single workspace
                </span>
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={8} className="w-72 p-0">
        <div className="border-b p-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
              EC
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{organizationName}</p>
              <p className="text-xs text-muted-foreground">Current workspace</p>
            </div>
            <Check className="h-4 w-4 text-primary" aria-label="Selected workspace" />
          </div>
        </div>

        <MenuSection label="Workspace">
          <MenuLink
            to="/settings"
            active={pathname === '/settings'}
            icon={<Settings className="h-4 w-4" />}
          >
            Workspace Settings
          </MenuLink>
          <MenuLink
            to="/activity"
            active={pathname === '/activity'}
            icon={<Activity className="h-4 w-4" />}
          >
            Activity
          </MenuLink>
          <MenuLink
            to="/alice-framework"
            active={pathname === '/alice-framework'}
            icon={<Bot className="h-4 w-4" />}
          >
            Alice Framework
          </MenuLink>
        </MenuSection>

        <div className="border-t p-3">
          <div className="mb-2 flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-xs font-semibold">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{accountLabel}</p>
              {userEmail && userEmail !== accountLabel && (
                <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
              )}
            </div>
          </div>
          <div className="space-y-0.5">
            <MenuLink
              to="/profile"
              active={pathname === '/profile'}
              icon={<UserRound className="h-4 w-4" />}
            >
              User Settings
            </MenuLink>
            <button
              type="button"
              className="flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-destructive transition-colors hover:bg-destructive/10"
              onClick={onSignOut}
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sign Out
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MenuSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="p-2">
      <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function MenuLink({
  to,
  active,
  icon,
  children,
}: {
  to: string;
  active: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'flex min-h-9 items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted',
        active && 'bg-primary/10 font-medium text-primary',
      )}
    >
      {icon}
      {children}
    </Link>
  );
}
